import { supabase } from '@/lib/supabase';

export type OfferStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'expired';
export type OfferKind = 'service' | 'goods';

export type DiscountType = 'amount' | 'percent';

export type OfferLineItem = {
  description: string;
  quantity: number;
  unitPrice: number;
  /** Προαιρετική έκπτωση για τη συγκεκριμένη γραμμή — τιμή σε € ή % ανάλογα με το discountType. */
  discount?: number;
  discountType?: DiscountType;
};

/** Ποσό έκπτωσης σε € για μία γραμμή (μετατρέπει % σε € βάσει του υποσυνόλου της γραμμής). */
export function lineItemDiscountAmount(item: OfferLineItem): number {
  const lineSubtotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
  const discount = Number(item.discount) || 0;
  if (discount <= 0) return 0;
  if (item.discountType === 'percent') {
    return Math.min(lineSubtotal, Math.round(lineSubtotal * (discount / 100) * 100) / 100);
  }
  return Math.min(lineSubtotal, discount);
}

/** Τελικό ποσό γραμμής μετά την έκπτωση. */
export function lineItemTotal(item: OfferLineItem): number {
  const lineSubtotal = (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0);
  return Math.max(0, Math.round((lineSubtotal - lineItemDiscountAmount(item)) * 100) / 100);
}

export type Offer = {
  id: string;
  creator_id: string;
  title: string;
  description: string | null;
  cost: number | null;
  currency: string;
  delivery_days: number | null;
  status: OfferStatus;
  agreement_id: string | null;
  recipient_email: string | null;
  recipient_id: string | null;
  recipient_name: string | null;
  invite_code: string | null;
  created_at: string;
  expires_at: string | null;
  include_professional_terms: boolean;
  offer_kind: OfferKind;
  line_items: OfferLineItem[] | null;
  service_request_id: string | null;
  sender_business_id: string | null;
  decline_reason?: string | null;
};

/** Μια προσφορά "έκλεισε με άλλον" όταν απορρίφθηκε αυτόματα επειδή ο αιτών
 * αποδέχτηκε άλλη προσφορά στο ίδιο request — ξεχωρίζει από μια χειροκίνητη
 * απόρριψη γιατί το decline_offer RPC πάντα γράφει decline_reason (έστω '' ),
 * ενώ το μαζικό UPDATE του accept_offer δεν το αγγίζει καθόλου (μένει null). */
export function isClosedByOther(o: Offer): boolean {
  return o.status === 'declined' && !!o.service_request_id && o.decline_reason == null;
}

export type NewOfferInput = {
  title: string;
  description?: string | null;
  cost?: number | null;
  currency?: string;
  delivery_days?: number | null;
  recipient_email?: string | null;
  recipient_id?: string | null;
  recipient_name?: string | null;
  type?: 'one_off' | 'ongoing' | 'fixed_term';
  start_date?: string | null;
  end_date?: string | null;
  includeProfessionalTerms?: boolean;
  offerKind?: OfferKind;
  lineItems?: OfferLineItem[] | null;
  serviceRequestId?: string | null;
  /** Αν οριστεί, η προσφορά στέλνεται με την ταυτότητα αυτής της επιχείρησης
   * (όνομα/λογότυπο) αντί για το προσωπικό προφίλ σου — για συνεταίρους που
   * έχουν και δικό τους ξεχωριστό επάγγελμα. */
  senderBusinessId?: string | null;
};

/** Άθροισμα γραμμών (τεμάχια × τιμή μονάδας), συνολική έκπτωση, και τελικό σύνολο. */
export function computeLineItemsTotals(items: OfferLineItem[]): { subtotal: number; discountTotal: number; finalTotal: number } {
  let subtotal = 0;
  let discountTotal = 0;
  for (const it of items) {
    subtotal += (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0);
    discountTotal += lineItemDiscountAmount(it);
  }
  const finalTotal = Math.max(0, Math.round((subtotal - discountTotal) * 100) / 100);
  return { subtotal: Math.round(subtotal * 100) / 100, discountTotal: Math.round(discountTotal * 100) / 100, finalTotal };
}

// service_request_id δεν υπάρχει ακόμα στα auto-generated database.types.ts —
// bypass μέχρι να τρέξει νέα γενιά τύπων.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function createOffer(input: NewOfferInput): Promise<Offer> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Δεν είσαι συνδεδεμένος.');

  const { data, error } = await db
    .from('offers')
    .insert({
      creator_id: user.id,
      title: input.title,
      description: input.description ?? null,
      cost: input.cost ?? null,
      currency: input.currency ?? 'EUR',
      delivery_days: input.delivery_days ?? null,
      recipient_email: input.recipient_email ?? null,
      recipient_id: input.recipient_id ?? null,
      recipient_name: input.recipient_name ?? null,
      type: input.type ?? 'one_off',
      start_date: input.start_date ?? null,
      end_date: input.end_date ?? null,
      include_professional_terms: input.includeProfessionalTerms ?? true,
      offer_kind: input.offerKind ?? 'service',
      line_items: input.lineItems ?? null,
      service_request_id: input.serviceRequestId ?? null,
      sender_business_id: input.senderBusinessId ?? null,
      status: 'sent',
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data as Offer;
}

export async function listMyOffers(): Promise<Offer[]> {
  const { data, error } = await db
    .from('offers')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as Offer[];
}

export async function listPendingOffers(): Promise<Offer[]> {
  const { data, error } = await db
    .from('offers')
    .select('*')
    .eq('status', 'sent')
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);
  return (data ?? []) as Offer[];
}

/** Προσφορές που περιμένουν ΕΜΑ να αποδεχτώ/αρνηθώ (εισερχόμενες, δεν τις έστειλα εγώ). */
export async function countPendingOffersForMe(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.email) return 0;
  const { count, error } = await supabase
    .from('offers')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'sent')
    .neq('creator_id', user.id)
    .eq('recipient_email', user.email);
  if (error) return 0;
  return count ?? 0;
}

export async function getOfferPreview(offerId: string): Promise<{
  id: string;
  title: string;
  description: string | null;
  cost: number | null;
  delivery_days: number | null;
  creator_name: string;
  creator_avatar: string | null;
  offer_kind: OfferKind;
  line_items: OfferLineItem[] | null;
} | null> {
  const { data: offer, error } = await supabase
    .from('offers')
    .select('id, title, description, cost, delivery_days, creator_id, status, offer_kind, line_items')
    .eq('id', offerId)
    .single() as { data: Offer | null; error: unknown };

  if (error || !offer) return null;
  if (!['sent'].includes(offer.status)) return null;

  const { data: creatorRows } = await supabase.rpc('get_profiles_public', { p_ids: [offer.creator_id] });
  const creator = creatorRows?.[0] ?? null;

  return {
    id: offer.id,
    title: offer.title,
    description: offer.description,
    cost: offer.cost,
    delivery_days: offer.delivery_days,
    creator_name: creator?.full_name?.trim() || 'Χρήστης',
    creator_avatar: creator?.avatar_url ?? null,
    offer_kind: (offer as any).offer_kind ?? 'service',
    line_items: (offer as any).line_items ?? null,
  };
}

export async function acceptOffer(offerId: string): Promise<string> {
  const { data, error } = await supabase.rpc('accept_offer', { p_offer_id: offerId });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function declineOffer(offerId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('decline_offer', {
    p_offer_id: offerId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function cloneOfferAsDraft(offerId: string): Promise<string> {
  const { data, error } = await supabase.rpc('clone_offer_as_draft', { p_offer_id: offerId });
  if (error) throw new Error(error.message);
  return data;
}

/** Προσφορές που έλαβε ο ιδιοκτήτης ενός συγκεκριμένου αιτήματος ("Ζητώ..."). */
export async function listOffersForRequest(requestId: string): Promise<Offer[]> {
  const { data, error } = await supabase.rpc('list_offers_for_request' as never, { p_request_id: requestId } as never);
  if (error) throw new Error(error.message);
  return (data ?? []) as Offer[];
}

export async function listIncomingOffers(): Promise<Offer[]> {
  const { data, error } = await db.rpc('list_incoming_offers');
  if (error) throw new Error(error.message);
  return (data ?? []) as Offer[];
}

